import React from "react";
import { useTranslation } from "react-i18next";
import { PiWarningCircleLight } from "react-icons/pi";
import { useAppDispatch } from "../../store";
import { showModal, hideModal } from "../../store/actions/modalActions";
import {
  AIProgressPanel,
  AIRun,
  AIStage,
  AIStageStatus,
  aiStageKey,
  aiStageLabel,
  initialAIRun,
} from "./brand/aiProgress";

export type UiStepStatus = "Pending" | "In Progress" | "Completed" | "Failed" | "Skipped";

export interface IndexStep {
  id: string;
  label: string;
  status: UiStepStatus;
  messages: string[];
}

interface Props {
  title?: string;
  steps: IndexStep[];
  connected: boolean;
  complete: boolean;
  isConnecting: boolean;
  ok: boolean | null;
  summary?: string;
  startedAt?: string | number;
  onCancel?: () => void;
  type?: string;
}

const STEP_STATUS: Record<UiStepStatus, AIStageStatus> = {
  Pending: "pending",
  "In Progress": "active",
  Completed: "done",
  Failed: "failed",
  Skipped: "skipped",
};

/**
 * The creation/indexing progress view for tables, data sources, templates and
 * file uploads - now a thin adapter over the shared AI panel.
 *
 * Two things went with the old body. The `bg-gradient-to-r from-info-500
 * to-info-900` bar on a `bg-gray-200` track, and the number that filled it:
 * `(done + inProgress * 0.15) / (total + 1)`, a formula whose only purpose was
 * to make the bar move. Callers hand over real step statuses; the panel shows
 * those, plus an indeterminate sliver while there is no measurement to show.
 */
const SSECreationProgress: React.FC<Props> = ({
  title = "Source creation in progress",
  steps,
  connected,
  complete,
  isConnecting,
  ok,
  summary,
  startedAt,
  onCancel,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [elapsed, setElapsed] = React.useState<number>(0);

  React.useEffect(() => {
    if (!startedAt) return;
    const startMs =
      typeof startedAt === "string" ? new Date(startedAt).getTime() : Number(startedAt);
    if (!Number.isFinite(startMs)) return;
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const handleCancelWithConfirmation = () => {
    dispatch(
      showModal({
        showModal: true,
        type: "confirmation",
        title: t("Cancel Process"),
        message: t(
          "This will stop the current process and any data created during this process may be deleted."
        ),
        icon: <PiWarningCircleLight className="w-6 h-6 text-warning-600" />,
        variant: "danger",
        confirmLabel: t("Cancel Process"),
        onConfirm: () => {
          dispatch(hideModal());
          onCancel?.();
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  const run: AIRun = React.useMemo(() => {
    const stages: AIStage[] = steps.map((s, i) => {
      const messages = (s.messages || []).filter((m) => typeof m === "string" && m.trim());
      return {
        key: aiStageKey(s.id) || `step_${i}`,
        label: s.label || aiStageLabel(s.id),
        status: complete ? "done" : STEP_STATUS[s.status] ?? "active",
        startedAt: 0,
        ...(messages.length ? { message: messages[messages.length - 1] } : {}),
      };
    });

    const failed = complete && ok === false;
    return {
      ...initialAIRun,
      status: failed
        ? "failed"
        : complete
          ? "completed"
          : connected && stages.length
            ? "running"
            : "connecting",
      stages,
      // The only honest numbers this flow has: none while it runs, 100 when it
      // is over. Everything in between is the stage list's job.
      percent: complete && !failed ? 100 : null,
      message: failed ? null : summary || null,
      error: failed ? summary || t("Failed") : null,
      elapsedSeconds: startedAt ? elapsed : 0,
    };
  }, [steps, complete, connected, ok, summary, startedAt, elapsed, t]);

  return (
    <AIProgressPanel
      run={run}
      layout="centered"
      title={t(title)}
      completedTitle={t("Completed")}
      failedTitle={t("Failed")}
      idleMessage={isConnecting ? t("Connecting to the processing pipeline…") : undefined}
      onCancel={onCancel ? handleCancelWithConfirmation : undefined}
    />
  );
};

export default SSECreationProgress;
